import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { registerForPush } from "../lib/firebase";
import { SKIN_TYPES, SKIN_TONES, HAIR_TYPES, CONCERNS, GOALS } from "../data/onboarding";
import { LogOut, Sparkles, Loader2, Crown, Trash2, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { profile, signOut, refreshProfile, user } = useAuth();
  const navigate = useNavigate();
  const [edit, setEdit] = useState({
    name: profile?.name || "",
    skin_type: profile?.skin_type || "",
    skin_tone: profile?.skin_tone || "",
    hair_type: profile?.hair_type || "",
    concerns: profile?.concerns || [],
    goals: profile?.goals || [],
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Hydrate the edit form when the async profile becomes available so the
  // form never renders empty (which would silently wipe user data on save).
  useEffect(() => {
    if (!profile) return;
    setEdit({
      name: profile.name || "",
      skin_type: profile.skin_type || "",
      skin_tone: profile.skin_tone || "",
      hair_type: profile.hair_type || "",
      concerns: profile.concerns || [],
      goals: profile.goals || [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, profile?.name, profile?.skin_type, profile?.skin_tone, profile?.hair_type, profile?.concerns, profile?.goals]);

  const toggle = (key, value) => {
    setEdit((d) => {
      const arr = d[key] || [];
      return { ...d, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };

  const save = async () => {
    if (!user?.id) return;
    setSaving(true);
    await supabase.from("users").update(edit).eq("id", user.id);
    await refreshProfile();
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  const retakeQuiz = async () => {
    if (!user?.id) return;
    await supabase.from("users").update({ onboarding_complete: false }).eq("id", user.id);
    await refreshProfile();
    navigate("/onboarding");
  };

  const deleteAccount = async () => {
    if (!user?.id) return;
    const sure = window.confirm("Delete your account permanently? This cannot be undone.");
    if (!sure) return;
    await supabase.from("users").delete().eq("id", user.id);
    await signOut();
    navigate("/");
  };

  return (
    <div className="px-5 pt-2 pb-10" data-testid="profile-page">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-1">Settings</p>
          <h1 className="font-serif text-3xl text-cocoa">Profile</h1>
        </div>
        {profile?.is_premium && (
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white"
            style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
            data-testid="profile-premium-badge"
          >
            <Crown size={11} /> Premium
          </div>
        )}
      </div>

      <div className="bg-white rounded-[20px] p-5 shadow-card mb-4 space-y-4">
        <Field label="First name">
          <input
            value={edit.name}
            onChange={(e) => setEdit({ ...edit, name: e.target.value })}
            data-testid="profile-name"
            className="w-full rounded-full bg-cream px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose/40"
          />
        </Field>

        <Field label="Skin type">
          <div className="flex flex-wrap gap-2">
            {SKIN_TYPES.map((s) => (
              <button
                key={s.value}
                onClick={() => setEdit({ ...edit, skin_type: s.value })}
                data-testid={`profile-skin-${s.value}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  edit.skin_type === s.value ? "bg-rose text-white" : "bg-cream text-cocoa/70"
                }`}
              >
                {s.value}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Skin tone">
          <div className="grid grid-cols-6 gap-2">
            {SKIN_TONES.map((t) => (
              <button
                key={t.name}
                onClick={() => setEdit({ ...edit, skin_tone: t.name })}
                data-testid={`profile-tone-${t.name}`}
                className={`w-10 h-10 rounded-full transition-all ${
                  edit.skin_tone === t.name ? "ring-2 ring-gold ring-offset-2 ring-offset-white" : ""
                }`}
                style={{ background: t.hex }}
              />
            ))}
          </div>
        </Field>

        <Field label="Hair type">
          <div className="flex flex-wrap gap-2">
            {HAIR_TYPES.map((h) => (
              <button
                key={h}
                onClick={() => setEdit({ ...edit, hair_type: h })}
                data-testid={`profile-hair-${h}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  edit.hair_type === h ? "bg-rose text-white" : "bg-cream text-cocoa/70"
                }`}
              >
                {h}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Concerns">
          <div className="flex flex-wrap gap-2">
            {[
              ...CONCERNS.skin.map((c) => ({ cat: "skin", label: c })),
              ...CONCERNS.hair.map((c) => ({ cat: "hair", label: c })),
            ].map(({ cat, label }) => {
              const key = `${cat}:${label}`;
              return (
                <button
                  key={key}
                  onClick={() => toggle("concerns", key)}
                  data-testid={`profile-concern-${cat}-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                    edit.concerns.includes(key) ? "bg-rose text-white" : "bg-cream text-cocoa/70"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Goals">
          <div className="flex flex-wrap gap-2">
            {GOALS.map((g) => (
              <button
                key={g}
                onClick={() => toggle("goals", g)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  edit.goals.includes(g) ? "bg-rose text-white" : "bg-cream text-cocoa/70"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </Field>

        <button
          onClick={save}
          disabled={saving}
          data-testid="profile-save"
          className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3 font-medium shadow-rose disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? "Saved ✓" : "Save changes"}
        </button>
      </div>

      <div className="space-y-3">
        <NotificationCard userId={user?.id} />
        <button
          onClick={retakeQuiz}
          data-testid="retake-quiz"
          className="w-full rounded-full bg-white border border-cocoa/10 py-3 font-medium text-cocoa flex items-center justify-center gap-2"
        >
          <Sparkles size={14} /> Retake onboarding quiz
        </button>
        <button
          onClick={async () => { await signOut(); navigate("/"); }}
          data-testid="logout-btn"
          className="w-full rounded-full bg-cocoa text-white py-3 font-medium flex items-center justify-center gap-2"
        >
          <LogOut size={14} /> Log out
        </button>
        <button
          onClick={deleteAccount}
          data-testid="delete-account"
          className="w-full rounded-full bg-transparent border border-red-200 text-red-500 py-3 font-medium flex items-center justify-center gap-2"
        >
          <Trash2 size={14} /> Delete account
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-cocoa/55 mb-2">{label}</p>
      {children}
    </div>
  );
}

function NotificationCard({ userId }) {
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState({
    notify_morning: true,
    notify_streak: true,
    notify_weekly_summary: true,
    notify_insights: true,
  });

  useEffect(() => {
    if (!userId) return;
    supabase
      .from("fcm_tokens")
      .select("notify_morning, notify_streak, notify_weekly_summary, notify_insights")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => { if (data) setPrefs(data); });
  }, [userId]);

  const enable = async () => {
    setBusy(true);
    const token = await registerForPush(userId);
    setPerm(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
    setBusy(false);
    if (token) toast.success("Notifications enabled");
    else if (Notification.permission === "denied") toast.error("Blocked in browser settings");
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    if (userId) {
      await supabase.from("fcm_tokens").update(next).eq("user_id", userId);
    }
  };

  return (
    <div className="bg-white rounded-[20px] p-5 shadow-card" data-testid="notification-card">
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: perm === "granted" ? "linear-gradient(135deg,#C9748A,#E8B4C2)" : "#F5ECE6" }}
        >
          {perm === "granted" ? <Bell size={18} className="text-white" /> : <BellOff size={18} className="text-cocoa/50" />}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-cocoa">Push notifications</p>
          <p className="text-xs text-cocoa/55 font-light">
            {perm === "granted" ? "On — we'll nudge you at the right moments." :
             perm === "denied" ? "Blocked. Re-enable in browser settings." :
             "Off. Turn on to get reminders."}
          </p>
        </div>
        {perm !== "granted" && perm !== "denied" && perm !== "unsupported" && (
          <button
            onClick={enable}
            disabled={busy}
            data-testid="profile-enable-notifications"
            className="rounded-full bg-rose text-white text-xs px-4 py-2 font-medium shadow-rose disabled:opacity-50"
          >
            {busy ? "…" : "Enable"}
          </button>
        )}
      </div>

      {perm === "granted" && (
        <div className="space-y-2 pt-3 border-t border-border">
          <PrefToggle label="Morning ritual (7am)" value={prefs.notify_morning} onChange={() => togglePref("notify_morning")} testId="pref-morning" />
          <PrefToggle label="Streak reminder (8pm)" value={prefs.notify_streak} onChange={() => togglePref("notify_streak")} testId="pref-streak" />
          <PrefToggle label="Weekly summary (Sunday)" value={prefs.notify_weekly_summary} onChange={() => togglePref("notify_weekly_summary")} testId="pref-weekly" premium />
          <PrefToggle label="Daily AI insight" value={prefs.notify_insights} onChange={() => togglePref("notify_insights")} testId="pref-insights" premium />
        </div>
      )}
    </div>
  );
}

function PrefToggle({ label, value, onChange, testId, premium }) {
  return (
    <label className="flex items-center justify-between text-sm text-cocoa cursor-pointer">
      <span className="flex items-center gap-1.5 font-light">
        {label}
        {premium && <Crown size={11} className="text-gold" />}
      </span>
      <button
        type="button"
        onClick={onChange}
        data-testid={testId}
        className={`w-10 h-6 rounded-full transition-all relative ${value ? "bg-rose" : "bg-cocoa/15"}`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${
            value ? "left-[18px]" : "left-0.5"
          }`}
        />
      </button>
    </label>
  );
}
